import { listMonths, readMonth } from "./inventory";

const API_BASE = "https://openrouter.ai/api/v1";

function getKey(): string {
  const key = import.meta.env.VITE_OPENROUTER_KEY;
  if (!key) throw new Error("VITE_OPENROUTER_KEY not set in .env");
  return key as string;
}

const SYSTEM_PROMPT = `You are a stock/inventory analysis assistant for Mavelux paints, a paint manufacturing company in Nigeria. You have access to the complete ledger data across all available months.

Your task is to answer questions about inventory, spending, quantities, and trends. The data is organized by month (YYYY-MM format) and each month has three sections: raw_materials, packaging, and finished_goods.

Guidelines:
1. All currency is Nigerian Naira (₦).
2. Be precise and cite specific numbers and months from the data.
3. If a question asks about a time period ("2 months ago", "last quarter"), calculate the correct months based on today's date and the available data.
4. If you cannot find the answer in the data, say so clearly.
5. Format responses in clear markdown for readability — use tables, bold, bullet points as appropriate.
6. Show which months and sections you looked at.`;

export async function askClaude(question: string): Promise<string> {
  const months = await listMonths();

  const allData: Record<string, unknown> = {};
  await Promise.all(
    months.map(async (m) => {
      allData[m] = await readMonth(m);
    })
  );

  const payload = {
    model: "anthropic/claude-sonnet-4",
    messages: [
      {
        role: "system",
        content: SYSTEM_PROMPT + "\n\nToday's date: " + new Date().toISOString().slice(0, 10) + "\n\nAvailable ledger data:\n\n" + JSON.stringify(allData, null, 2),
      },
      { role: "user", content: question },
    ],
    temperature: 0.1,
    max_tokens: 4096,
  };

  const res = await fetch(`${API_BASE}/api/v1/chat/completions`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${getKey()}`,
    },
    body: JSON.stringify(payload),
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "unknown error");
    throw new Error(`OpenRouter ${res.status}: ${text}`);
  }

  const json = await res.json();
  return json.choices?.[0]?.message?.content ?? "No response.";
}
